import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	GovControlGroup,
	GovDropdown,
	GovIcon,
	GovTooltip,
} from '@gov-design-system-ce/react';
import { Checkbox } from '@gov-design-system-ce/react-form';
import type { DatovkaApiType } from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import {
	DATABOX_FOLDER_LABELS,
	DataBoxConfirmModalType,
	DataBoxFolderType,
	dataBoxLabel,
	DataBoxListStatesType,
	getDataBoxIcon,
	getDataBoxInitials,
	isDataBoxTypePO,
	useDataBoxMessageListContext,
	useDataBoxSearchFormContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button, Space } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { DataBoxSearch } from './DataBoxSearch';
import { DataBoxArchiveRemoveDropdown } from './DataBoxArchiveRemoveDropdown';

export const DataBoxMessageListHeader = () => {
	const { t } = useTranslation([PageCode['datove-schranky']]);

	const {
		dataBox,
		storage,
		messagesFilter: { dataBoxId, folderType, query, hasSearch },
		messageList,
		messageListCount,
		massOperations: { handleArchiveAllMessages },
		listState,
		setListState,
		messagesSelect: {
			showCheckboxes,
			updateAllMessagesSelection,
			isAllSelected,
			isPartialySelected,
		},
		handleOpenMobileMenu,
		setConfirmModal,
		controls,
	} = useDataBoxMessageListContext();
	const { form } = useDataBoxSearchFormContext();
	const { extraConfig } = useExtraConfigContext();

	const navigate = useNavigate();

	const isSearchMass = [
		DataBoxListStatesType.SearchMassArchive,
		DataBoxListStatesType.SearchMassPermanentRemove,
		DataBoxListStatesType.SearchMassRemove,
		DataBoxListStatesType.SearchMassRestore,
	].includes(listState);

	return (
		<header
			className={
				hasSearch
					? 'flex flex-col gap-(--templates-margin-s) py-(--templates-margin-s) px-(--spacing-m) mt-0! bg-(--background-primary-subtle) [ ds:rounded-t-3xl ]'
					: cx(
							'flex justify-between items-center gap-1 h-(--height-5xl) py-(--templates-margin-s) px-(--spacing-m) mt-0! bg-(--background-primary-subtle) mt-0! [ ds:rounded-t-3xl ] [ xl:bg-(--background-block-primary) ]',
							showCheckboxes && 'bg-(--background-primary-subtle)!'
						)
			}>
			{!hasSearch && (
				<div className="flex gap-2">
					<Button
						color="primary"
						type="link"
						size="m"
						onClick={handleOpenMobileMenu}
						className="block relative ds-no-underline [ xl:hidden! ]"
						aria-label={t('wcag.otevrit-postranni-panel')}
						aria-expanded={true}
						aria-controls="ds-menu">
						<div className="flex flex-col justify-center items-center w-10 h-10 rounded-full bg-(--background-white) text-(--text-primary)">
							<GovIcon
								name={
									getDataBoxIcon(
										dataBox?.typSchranky as DatovkaApiType['typSchranky']
									) as string
								}
								type="templates"
								size="s"
								className="relative top-0.5"
							/>
							<h2
								className="text-body-s-bold!"
								aria-label={dataBox ? dataBoxLabel(dataBox, true) : undefined}>
								{dataBox && getDataBoxInitials(dataBox)}
							</h2>
						</div>
					</Button>
					<div className="flex items-center">
						{showCheckboxes && (
							<Checkbox
								name="all-messages"
								size="m"
								isChecked={isAllSelected}
								indeterminate={isPartialySelected}
								onChange={() => updateAllMessagesSelection(!isAllSelected)}
								hasNoLabel={true}
							/>
						)}
						<div>
							<h1 className="flex items-center gap-(--spacing-s) text-body-xl-bold!">
								{t(DATABOX_FOLDER_LABELS[folderType])}
							</h1>
							{folderType === DataBoxFolderType.Archived &&
								listState === DataBoxListStatesType.List &&
								storage.storageUsage?.isBeingUsed && (
									<span className="block text-body-xs">
										{t('uloziste.vyuzito')}
										<Space />
										{storage.storageUsage?.usedFormattedShort}
										<Space />
										<GovTooltip placement="bottom">
											<GovTooltip.Trigger>
												{t('uloziste.z')}
												<Space />
												{storage.storageUsage?.maximumFormattedShort}
											</GovTooltip.Trigger>
											<GovTooltip.Content>
												{t('uloziste.celkove-uloziste', {
													'maximalni-kapacita':
														storage.storageUsage?.maximumFormattedShort,
												})}
											</GovTooltip.Content>
										</GovTooltip>
									</span>
								)}
						</div>
					</div>
				</div>
			)}

			<div className="flex items-center gap-2">
				{!isDataBoxTypePO(dataBox) &&
					[DataBoxFolderType.Received, DataBoxFolderType.Sent].includes(
						folderType
					) &&
					listState === DataBoxListStatesType.List &&
					extraConfig.pageRoutes.databoxConfig.displayArchiveAction && (
						<GovControlGroup
							color="primary"
							className="w-auto border-hidden! [ xl:border-solid! ]">
							<Button
								color="primary"
								type="base"
								size="s"
								isDisabled={!messageList.length}
								onClick={handleArchiveAllMessages}
								className={cx(
									!messageList.length && 'border-r-primary-300!',
									'hidden! [ xl:inline-block! ]'
								)}
								aria-label={t('zpravy.vypis.archivovat-vse')}
								iconStart={
									<GovIcon
										name="archive"
										type="templates"
									/>
								}>
								{t('zpravy.vypis.archivovat-vse')}
							</Button>
							<GovDropdown
								position="right"
								color="primary"
								type="base"
								size="s"
								disabled={!messageList.length}
								label={
									<>
										<GovIcon
											name="chevron-down"
											type="components"
											className="hidden! [ xl:inline-flex! ]"
										/>
										<GovIcon
											name="archive"
											type="templates"
											className="[ xl:hidden! ]"
										/>
									</>
								}>
								<Button
									color="primary"
									type="base"
									size="s"
									isExpanded
									isDisabled={!messageList.length}
									className="button-fixed-width"
									onClick={handleArchiveAllMessages}
									aria-label={t('zpravy.vypis.archivovat-vse')}
									iconStart={
										<GovIcon
											name="archive"
											type="templates"
										/>
									}>
									{t('zpravy.vypis.archivovat-vse')}
								</Button>
								<Button
									color="primary"
									type="base"
									size="s"
									isExpanded
									isDisabled={!messageList.length}
									className="button-fixed-width"
									onClick={() => {
										setListState(DataBoxListStatesType.MassArchive);
										navigate(
											pageRouteResolver('datove-schranky.schranka', {
												dataBoxId,
												folderType,
											})
										);
									}}
									aria-label={t('zpravy.vypis.archivovat-vybrane')}
									iconStart={
										<GovIcon
											name="archive"
											type="templates"
										/>
									}>
									{t('zpravy.vypis.archivovat-nektere')}
								</Button>
							</GovDropdown>
						</GovControlGroup>
					)}

				{folderType === DataBoxFolderType.Archived &&
					listState === DataBoxListStatesType.List && (
						<DataBoxArchiveRemoveDropdown
							isDisabled={!messageList.length}
							onDeleteSomeMessages={() => {
								setListState(DataBoxListStatesType.MassRemove);
								navigate(
									pageRouteResolver('datove-schranky.schranka', {
										dataBoxId,
										folderType,
									})
								);
							}}
							onDeleteAllMessages={() => {
								setConfirmModal(DataBoxConfirmModalType.removeAllMessages);
							}}
						/>
					)}

				{folderType === DataBoxFolderType.Trash &&
					listState === DataBoxListStatesType.List && (
						<>
							<Button
								color="primary"
								type="base"
								size="s"
								isDisabled={!messageList.length}
								className="[ xl:hidden! ]"
								onClick={() => {
									setListState(DataBoxListStatesType.MassRestore);
									navigate(
										pageRouteResolver('datove-schranky.schranka', {
											dataBoxId,
											folderType,
										})
									);
								}}
								aria-label={t('zpravy.vypis.hromadne-obnoveni')}
								iconStart={
									<GovIcon
										name="arrow-counterclockwise"
										type="templates"
									/>
								}
							/>
							<GovControlGroup
								color="primary"
								className="w-auto border-hidden! [ xl:border-solid! ]">
								<Button
									color="primary"
									type="base"
									size="s"
									isDisabled={!messageList.length}
									onClick={() => {
										setConfirmModal(DataBoxConfirmModalType.emptyTrash);
									}}
									className={cx(
										!messageList.length && 'border-r-primary-300!',
										'hidden! [ xl:inline-block! ]'
									)}
									aria-label={t('zpravy.vypis.trvale-smazat-zobrazene')}
									iconStart={
										<GovIcon
											name="trash"
											type="templates"
										/>
									}>
									{t('zpravy.vypis.trvale-smazat-zobrazene')}
								</Button>
								<GovDropdown
									position="right"
									color="primary"
									type="base"
									size="s"
									disabled={!messageList.length}
									label={
										<>
											<GovIcon
												name="chevron-down"
												type="components"
												className="hidden! [ xl:inline-flex! ]"
											/>
											<GovIcon
												name="trash"
												type="templates"
												className="[ xl:hidden! ]"
											/>
										</>
									}>
									<Button
										color="error"
										type="base"
										size="s"
										isExpanded
										isDisabled={!messageList.length}
										className="button-fixed-width"
										onClick={() => {
											setConfirmModal(DataBoxConfirmModalType.emptyTrash);
										}}
										aria-label={t('zpravy.vypis.trvale-smazat-zobrazene')}
										iconStart={
											<GovIcon
												name="trash"
												type="templates"
											/>
										}>
										{t('zpravy.vypis.trvale-smazat-zobrazene')}
									</Button>
									<Button
										color="error"
										type="base"
										size="s"
										isExpanded
										isDisabled={!messageList.length}
										className="button-fixed-width"
										onClick={() => {
											setListState(DataBoxListStatesType.MassPermanentRemove);
											navigate(
												pageRouteResolver('datove-schranky.schranka', {
													dataBoxId,
													folderType,
												})
											);
										}}
										aria-label={t('zpravy.vypis.trvale-smazat-nektere')}
										iconStart={
											<GovIcon
												name="trash"
												type="templates"
											/>
										}>
										{t('zpravy.vypis.trvale-smazat-nektere')}
									</Button>
									<Button
										color="primary"
										type="base"
										size="s"
										isExpanded
										isDisabled={!messageList.length}
										className="button-fixed-width hidden! [ xl:block! ]"
										onClick={() => {
											setListState(DataBoxListStatesType.MassRestore);
											navigate(
												pageRouteResolver('datove-schranky.schranka', {
													dataBoxId,
													folderType,
												})
											);
										}}
										aria-label={t('zpravy.vypis.hromadne-obnoveni')}
										iconStart={
											<GovIcon
												name="arrow-counterclockwise"
												type="templates"
											/>
										}>
										{t('zpravy.vypis.hromadne-obnoveni')}
									</Button>
								</GovDropdown>
							</GovControlGroup>
						</>
					)}

				{listState === DataBoxListStatesType.List &&
					folderType !== DataBoxFolderType.Trash && (
						<Button
							color="primary"
							type="outlined"
							size="s"
							isDisabled={!messageList.length}
							onClick={() => {
								setListState(DataBoxListStatesType.Search);
								navigate(
									pageRouteResolver('datove-schranky.schranka', {
										dataBoxId,
										folderType,
									})
								);
							}}
							aria-label={t('zpravy.vypis.wcag.hledat')}
							iconStart={
								<GovIcon
									name="search"
									type="components"
								/>
							}
						/>
					)}
			</div>

			{hasSearch && (
				<>
					<div className="flex items-center gap-4 mb-2">
						<DataBoxSearch folderType={folderType} />
						{listState !== DataBoxListStatesType.List && (
							<Button
								color="primary"
								type="base"
								size="s"
								onClick={() => {
									setListState(DataBoxListStatesType.List);
									form.resetField('query');
								}}
								aria-label={t('zpravy.zrusit')}
								iconStart={
									<GovIcon
										name="x-lg"
										type="components"
									/>
								}
							/>
						)}
					</div>

					<div className="flex items-center justify-between gap-1">
						{listState === DataBoxListStatesType.Search &&
							query &&
							query.length >= 3 &&
							!controls.processLoading && (
								<span className="text-body-s text-(--text-secondary) mr-auto">
									{t('vyhledavani.vysledku')}:{' '}
									<strong>{messageListCount}</strong>
								</span>
							)}

						{!!messageListCount && query && (
							<>
								{isSearchMass && (
									<Checkbox
										name="all-messages"
										isChecked={isAllSelected}
										size="s"
										onChange={(event) => {
											updateAllMessagesSelection(event.target.checked);
										}}
										label={{
											children: t('vyhledavani.vsechny-vyhledane'),
										}}
									/>
								)}
								<span>
									{listState === DataBoxListStatesType.Search &&
										extraConfig.pageRoutes.databoxConfig.massActionsAllowedFolders.includes(
											folderType
										) && (
											<Button
												color="primary"
												type="base"
												size="s"
												onClick={() => {
													if (folderType === DataBoxFolderType.Archived) {
														setListState(
															DataBoxListStatesType.SearchMassRemove
														);
													} else if (folderType === DataBoxFolderType.Trash) {
														setListState(
															DataBoxListStatesType.SearchMassPermanentRemove
														);
													} else {
														setListState(
															DataBoxListStatesType.SearchMassArchive
														);
													}
												}}
												aria-label={t('zpravy.vypis.hromadne-obnoveni')}
												iconStart={
													<GovIcon
														name="check-multiple"
														type="templates"
													/>
												}>
												{t('zpravy.vypis.hromadne-obnoveni')}
											</Button>
										)}
									{isSearchMass && (
										<Button
											color="primary"
											type="base"
											size="s"
											onClick={() => {
												setListState(DataBoxListStatesType.Search);
											}}
											aria-label={t('zpravy.vypis.ukoncit-hromadne-akce')}>
											{t('zpravy.vypis.ukoncit-hromadne-akce')}
										</Button>
									)}
								</span>
							</>
						)}
					</div>
				</>
			)}

			{listState !== DataBoxListStatesType.List && !hasSearch && (
				<Button
					color="primary"
					type="base"
					size="s"
					onClick={() => {
						setListState(DataBoxListStatesType.List);
					}}
					aria-label={t('zpravy.zrusit')}
					iconStart={
						<GovIcon
							name="x-lg"
							type="components"
						/>
					}
				/>
			)}
		</header>
	);
};
